import type { Injector } from '@angular/core';
import { inject, runInInjectionContext } from '@angular/core';
import {
    assertInjector,
    isResolvedProblemDetails,
    qpxToast as toast,
} from '@quipex/core/utils';
import { BuildingsApiService } from '@quipex/shared/contracts';
import { DialogService, isDialogConfirmedOnly } from '@quipex/shared/ui';
import { map, take } from 'rxjs';
import { deleteDraftBuilding } from './api';

export interface DeleteDraftBuildingDialogRequest {
    buildingId: string;
    buildingName: string;
    restoreFocus?: string | HTMLElement;
    onDeleted?: () => void;
}

export interface DeleteDraftBuildingDialog {
    open: (request: DeleteDraftBuildingDialogRequest) => void;
}

interface ExecuteDraftDeleteParams {
    buildingsApi: BuildingsApiService;
    buildingId: string;
    onDeleted: DeleteDraftBuildingDialogRequest['onDeleted'];
}

export function injectDeleteDraftBuildingDialog(
    injector?: Injector
): DeleteDraftBuildingDialog {
    const resolvedInjector = assertInjector(
        injectDeleteDraftBuildingDialog,
        injector
    );

    const open = (request: DeleteDraftBuildingDialogRequest): void => {
        runInInjectionContext(resolvedInjector, () => {
            const dialogService = inject(DialogService);
            const buildingsApi = inject(BuildingsApiService);

            const buildingName = request.buildingName
                .replaceAll('&', '&amp;')
                .replaceAll('<', '&lt;')
                .replaceAll('>', '&gt;');
            const confirmationDialogRef = dialogService.openConfirmationDialog(
                {
                    status: 'destructive',
                    title: 'Delete building',
                    contentHtml: `<p>This will delete <strong>${buildingName}</strong>, including its building details and setup progress. This cannot be undone.</p>`,
                    confirmButton: 'Delete',
                    cancelButton: 'Cancel',
                },
                { restoreFocus: request.restoreFocus }
            );

            confirmationDialogRef
                .afterClosed()
                .pipe(
                    isDialogConfirmedOnly(),
                    take(1),
                    map(() => undefined)
                )
                .subscribe(() => {
                    executeDraftDelete({
                        buildingsApi,
                        buildingId: request.buildingId,
                        onDeleted: request.onDeleted,
                    });
                });
        });
    };

    return { open };
}

function executeDraftDelete(params: ExecuteDraftDeleteParams): void {
    const deletePromise = deleteDraftBuilding(
        params.buildingsApi,
        params.buildingId
    ).then(() => {
        params.onDeleted?.();
    });

    toast.promise(deletePromise, {
        loading: 'Deleting building...',
        success: 'Building deleted',
        error: (err: unknown) => toDeleteErrorMessage(err),
    });
}

function toDeleteErrorMessage(err: unknown): string {
    if (!isResolvedProblemDetails(err)) {
        return 'Failed to delete building';
    }

    return err.detail ?? err.title;
}
